import { type ReactNode, useCallback, useRef } from 'react'
import cx from 'classnames'
import styles from './SplitViewHeader.module.css'
import { Icon } from '@/components/Icon'
import { type MotionValue, useTransform } from 'motion/react'
import { ScrollShadow } from '@/components/ScrollShadow'
import { useFallbackMotionValue } from '@/hooks/motion-hooks'
import { clamp } from '@/utils/math-util'

interface SplitViewHeaderProps {
  children?: ReactNode
  compact?: boolean
  scrollOffset?: number | MotionValue<number>
  /**
   * If specified, renders a progress bar at the bottom border.
   */
  progress?: number
}

const DELTA = 64

/**
 * Header for a split view pane, with a shadow once the pane scrolls.
 */
export function SplitViewHeader({
  children,
  compact,
  scrollOffset,
  progress,
}: SplitViewHeaderProps) {
  const shadowOpacity = useTransform(
    useFallbackMotionValue(scrollOffset),
    (v) => clamp(v / DELTA),
  )
  return (
    <div className={cx(styles.root, compact && styles.compact)}>
      <div className={styles.content}>{children}</div>
      {scrollOffset !== undefined && (
        // We lift it up (z-index) because the scrolling
        // happens in a sibling container and not a parent, and
        // this element isn't sticky.
        <ScrollShadow lift borderless opacity={shadowOpacity} />
      )}
      {progress !== undefined && (
        <div
          className={styles.progress}
          style={{ transform: `scaleX(${clamp(progress / 100)})` }}
        />
      )}
    </div>
  )
}

interface SplitViewHeaderSearchProps {
  placeholder?: string
  value: string
  onChange: (newValue: string) => unknown
  withoutSearchIcon?: boolean
}

/**
 * A search field in a split view header.
 */
export function SplitViewHeaderSearch({
  placeholder = 'Search',
  onChange,
  value,
  withoutSearchIcon = false,
}: SplitViewHeaderSearchProps) {
  const searchFieldRef = useRef<HTMLInputElement>(null)
  const onSearchBar = useCallback(() => searchFieldRef.current?.focus(), [])
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- mouse convenience around the input, which is the keyboard target
    <div className={styles.searchBar} onClick={onSearchBar}>
      {!withoutSearchIcon && (
        <div className={styles.searchIcon}>
          <Icon name="search" />
        </div>
      )}
      <input
        ref={searchFieldRef}
        type="text"
        placeholder={placeholder}
        className={cx(styles.searchField, !value && styles.empty)}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  )
}

interface SplitViewHeaderTitleProps {
  children?: ReactNode
  title?: string
  testId?: string
}

/**
 * The title of a split view header.
 */
export function SplitViewHeaderTitle({
  children,
  title,
  testId,
}: SplitViewHeaderTitleProps) {
  return (
    <div className={styles.title} title={title} data-test={testId}>
      {children}
    </div>
  )
}

interface SplitViewHeaderSubtitleProps {
  children?: ReactNode
}

/**
 * The subtitle of a split view header.
 */
export function SplitViewHeaderSubtitle({
  children,
}: SplitViewHeaderSubtitleProps) {
  return <div className={styles.subtitle}>{children}</div>
}
